import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SeeDentistOptions from './pages/SeeDentist/SeeDentistOptions.js';

const Login = lazy(() => import("./pages/Login/Login"));
const About = lazy(() => import("./components/About/About"));
const Referrals = lazy(() => import("./pages/Referrals/Referrals"));
const Education = lazy(() => import("./pages/Education/Education"));
const Forms = lazy(() => import("./pages/Forms/Forms"));
const FormPrint = lazy(() => import("./pages/FormPrint/FormPrint"));
const Uploads = lazy(() => import("./pages/Uploads/Uploads"));
const Notes = lazy(() => import("./pages/Notes/Notes"));
const VisitHistory = lazy(() => import("./pages/VisitHistory/VisitHistory"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail/VerifyEmail"));

const ProfileSetting = lazy(() => import("./pages/ProfileSetting/ProfileSetting"));
const ChangePassword = lazy(() => import("./pages/ChangePassword/ChangePassword"));
const BillingInformation = lazy(() => import("./pages/BillingInformation/BillingInformation"));
const MyDependents = lazy(() => import("./pages/MyDependents/MyDependents.js"));
const SignUp = lazy(() => import("./pages/SignUp/SignUp"));
const RegistrationSuccess = lazy(() => import("./pages/RegistrationSuccess/RegistrationSuccess.js"));
const RegistrationConsentForm = lazy(() => import("./pages/RegistrationConsentForm/RegistrationConsentForm.js"));
const SetPassword = lazy(() => import("./pages/SetPassword/SetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword/ForgotPassword"));
const Prescription = lazy(() => import("./pages/Prescription/Prescription"));
const SeeDentist = lazy(() => import("./pages/SeeDentist/SeeDentist"));
const SeeDentistBilling = lazy(() => import("./pages/SeeDentist/SeeDentistBilling"));
const SeeDentistReview = lazy(() => import("./pages/SeeDentist/SeeDentistReview"));
const Layout = lazy(() => import("./pages/Layout/Layout"));
const RequireAuth = lazy(() => import("./helpers/RequireAuth"));
const AppointmentSchedule = lazy(() => import("./pages/AppointmentSchedule/AppointmentSchedule"));
const AppointmentScheduleBilling = lazy(() => import("./pages/AppointmentSchedule/AppointmentScheduleBilling"));
const AppointmentScheduleDateTimePage = lazy(() => import("./pages/AppointmentSchedule/AppointmentScheduleDateTimePage"));
const AppointmentScheduleReview = lazy(() => import("./pages/AppointmentSchedule/AppointmentScheduleReview"));
const AppointmentReschedule = lazy(() => import("./pages/AppointmentReschedule/AppointmentReschedule"));
const AppointmentCancel = lazy(() => import("./pages/AppointmentCancel/AppointmentCancel"));
const PrescriptionPrint = lazy(() => import("./components/Print/PrescriptionPrint"));
const NotesPrint = lazy(() => import("./components/Print/NotesPrint"));
const VisitHistoryPrint = lazy(() => import("./components/Print/VisitHistoryPrint"));
const FormSubmission = lazy(() => import("./pages/FormSubmission/FormSubmission"));
const SeeDentistPharmacySelection = lazy(() => import("./pages/SeeDentist/SeeDentistPharmacySelection"));
const AppointmentSchedulePharmacySelection = lazy(() => import("./pages/AppointmentSchedule/AppointmentSchedulePharmacySelection"));
const SurveyFormSubmission = lazy(() => import("./pages/SurveyFormSubmission/SurveyFormSubmission"));

const Instruction = lazy(() => import("./pages/Instruction/Instruction"));
const Questionnaire = lazy(() => import("./pages/Questionnaire/Questionnaire"));
const QuestionnaireRecordRequest = lazy(() => import("./pages/QuestionnaireRecordRequest/QuestionnaireRecordRequest"));
const QuestionnaireIntakeForm = lazy(() => import("./pages/QuestionnaireIntakeForm/QuestionnaireIntakeForm"));
const QuestionnaireReviewSubmit = lazy(() => import("./pages/QuestionnaireReviewSubmit/QuestionnaireReviewSubmit"));
const QuestionnaireBillingInfo = lazy(() => import("./pages/QuestionnaireBillingInfo/QuestionnaireBillingInfo"));
const QuestionnaireAfterPaymentSuccessful = lazy(() => import("./pages/QuestionnaireAfterPaymentSuccessful/QuestionnaireAfterPaymentSuccessful"));
const ConsentForm = lazy(() => import("./pages/ConsentForm/ConsentForm"));
const CaseReview = lazy(() => import("./pages/CaseReview/CaseReview"));
const RequestCompletedDetail = lazy(() => import("./pages/RequestCompletedDetail/RequestCompletedDetail"));
const QuestionnaireUpdateRecord = lazy(() => import("./pages/QuestionnaireUpdateRecord/QuestionnaireUpdateRecord"));
const QuestionnaireUploadRecords = lazy(() => import("./pages/QuestionnaireUpdateRecord/QuestionnaireUploadRecord"));
const SecondOpinionAppointment = lazy(() => import("./pages/SecondOpinionAppointment/SecondOpinionAppointment"));
const BookedAppointment = lazy(() => import("./pages/BookedAppointment/BookedAppointment"));
const UploadMoreRecords = lazy(() => import("./pages/UploadMoreRecords/UploadMoreRecords"));
const PatientRecordsRequest = lazy(() => import("./pages/PatientRecordsRequest/PatientRecordsRequest"));
const SecondOpinionAppointmentReschedule = lazy(() => import("./pages/SecondOpinionAppointmentReschedule/SecondOpinionAppointmentReschedule"));
const ResetPassword = lazy(() => import("./pages/ResetPassword/ResetPassword"));
const Insurance = lazy(() => import("./pages/Insurance/InsuranceForm"));
const InsuranceInformationForm = lazy(() =>
  import("./pages/InsuranceInformation/InsuranceForm")
);
const ShippingAddressForm = lazy(() =>
  import("./pages/ShippingAddressForm/ShippingAddressForm")
);

const SmartScanWorks = lazy(() => import("./pages/SmartScanWorks/SmartScanWorks"));
const SmartScanInstruction = lazy(() => import("./pages/SmartScanInstruction/SmartScanInstruction"));
const SmartScanLowerArch = lazy(() => import("./pages/SmartScanLowerArch/SmartScanLowerArch"));
const SmartScanLowerArchConfirm = lazy(() => import("./pages/SmartScanLowerArchConfirm/SmartScanLowerArchConfirm"));
const SmartScanLowerLip = lazy(() => import("./pages/SmartScanLowerLip/SmartScanLowerLip"));
const SmartScanLowerLipConfirm = lazy(() => import("./pages/SmartScanLowerLipConfirm/SmartScanLowerLipConfirm"));
const SmartScanUpperArch = lazy(() => import("./pages/SmartScanUpperArch/SmartScanUpperArch"));
const SmartScanUpperArchConfirm = lazy(() => import("./pages/SmartScanUpperArchConfirm/SmartScanUpperArchConfirm"));
const SmartScanUpperLip = lazy(() => import("./pages/SmartScanUpperLip/SmartScanUpperLip"));
const SmartScanUpperLipConfirm = lazy(() => import("./pages/SmartScanUpperLipConfirm/SmartScanUpperLipConfirm"));
const SmartScanBite = lazy(() => import("./pages/SmartScanBite/SmartScanBite"));
const SmartScanBiteConfirm = lazy(() => import("./pages/SmartScanBiteConfirm/SmartScanBiteConfirm"));
const SmartScanPose = lazy(() => import("./pages/SmartScanPose/SmartScanPose"));
const SmartScanSubmittedRecords = lazy(() => import("./pages/SmartScanSubmittedRecords/SmartScanSubmittedRecords"));
const SmartScanSelectPatient = lazy(() => import("./pages/SmartScanSelectPatient/SmartScanSelectPatient"));
const SmartScanDisclaimer = lazy(() => import("./pages/SmartScanDisclaimer/SmartScanDisclaimer"));
const SmartScanSubmitted = lazy(() => import("./pages/SmartScanSubmitted/SmartScanSubmitted"));
const WellnessScore = lazy(() => import("./pages/WellnessScore/WellnessScore"));
const WellnessScorePrint = lazy(() => import("./pages/WellnessScorePrint/WellnessScorePrint"));
const CancelPayment = lazy(() => import("./pages/CancelPayment/CancelPayment"));
const SmartScanPhotoOption = lazy(() => import("./pages/SmartScanPhotoOption/SmartScanPhotoOption"));
const Chat = lazy(() => import("./pages/Chat/Chat.js"));
const QuestionnaireInsuranceInfo = lazy(() => import("./pages/QuestionnaireInsuranceInfo/QuestionnaireInsuranceInfo.js"));
const DentistDirectLandingPage = lazy(() => import("./pages/DentistDirectLandingPage/DentistDirectLandingPage.js"));
const SmartScanSendResult = lazy(() => import("./pages/SmartScanSendResult/SmartScanSendResult.js"));
const SubmitVerificationCode = lazy(() => import("./pages/SubmitVerificationCode/SubmitVerificationCode.js"));
const FinishAccountSetup = lazy(() => import("./pages/FinishAccountSetup/FinishAccountSetup.js"));
const PhoneNumberLogin = lazy(() => import("./pages/Login/PhoneNumberLogin.js"));
const SignUpWithPhone = lazy(() => import("./pages/SignUp/SignUpWithPhone.js"));
const AnonymousChat = lazy(() => import("./pages/Chat/AnonymousChat.js"));
const SmartscanReport = lazy(() => import("./pages/SmartscanReport/SmartscanReport.js"));
const RedirectionToShortUrl = lazy(() => import("./pages/RedirectionToShortUrl/RedirectionToShortUrl.js"));
const Videos = lazy(() => import("./pages/Videos/Videos.js"));

const Navigation = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<div></div>}>
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path="/insurance" element={<Insurance />} />
            <Route path="/shipping-address" element={<ShippingAddressForm />} />
            <Route
              path="/registration-success"
              element={<RegistrationSuccess />}
            />
            <Route
              path="/registration-consent-form"
              element={<RegistrationConsentForm />}
            />
            <Route path="/about" element={<About />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:code" element={<VerifyEmail />} />
            <Route
              path="/survey-form-submission"
              element={<SurveyFormSubmission />}
            />
            <Route
              path="/patient-record-request"
              element={<PatientRecordsRequest />}
            />
            <Route path="/" element={<RequireAuth />} />
            <Route path="" element={<RequireAuth />} />
            {/* protected routes */}
            <Route
              path="/smart-scan-send-result"
              element={<SmartScanSendResult />}
            />
            <Route
              path="/submit-verification-code"
              element={<SubmitVerificationCode />}
            />
            <Route
              path="/finish-account-setup"
              element={<FinishAccountSetup />}
            />

          <Route
            path="/smart-scan-works-no-auth"
            element={<SmartScanWorks />}
          />
          <Route
            path="/smart-scan-disclaimer-no-auth"
            element={<SmartScanDisclaimer />}
          />
          <Route
            path="/smart-scan-photo-options-no-auth"
            element={<SmartScanPhotoOption />}
          />
          <Route
            path="/smart-scan-instruction-no-auth"
            element={<SmartScanInstruction />}
          />
          <Route
            path="/smart-scan-lower-arch-no-auth"
            element={<SmartScanLowerArch />}
          />
          <Route
            path="/smart-scan-lower-arch-confirm-no-auth"
            element={<SmartScanLowerArchConfirm />}
          />
          <Route
            path="/smart-scan-lower-lip-no-auth"
            element={<SmartScanLowerLip />}
          />
          <Route
            path="/smart-scan-lower-lip-confirm-no-auth"
            element={<SmartScanLowerLipConfirm />}
          />
          <Route
            path="/smart-scan-upper-arch-no-auth"
            element={<SmartScanUpperArch />}
          />
          <Route
            path="/smart-scan-upper-arch-confirm-no-auth"
            element={<SmartScanUpperArchConfirm />}
          />
          <Route
            path="/smart-scan-upper-lip-no-auth"
            element={<SmartScanUpperLip />}
          />
          <Route
            path="/smart-scan-upper-lip-confirm-no-auth"
            element={<SmartScanUpperLipConfirm />}
          />
          <Route path="/smart-scan-bite-no-auth" element={<SmartScanBite />} />
          <Route
            path="/smart-scan-bite-confirm-no-auth"
            element={<SmartScanBiteConfirm />}
          />
          <Route path="/smart-scan-pose-no-auth" element={<SmartScanPose />} />

          <Route path="/sign-in-with-phone" element={<PhoneNumberLogin />} />
          <Route path="/sign-up-with-phone" element={<SignUpWithPhone />} />
          <Route element={<RequireAuth />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/see-dentist-options" element={<SeeDentistOptions />} />
            <Route path="/see-dentist" element={<SeeDentist />} />
            <Route
              path="/see-dentist-pharmacy-selection"
              element={<SeeDentistPharmacySelection />}
            />
            <Route
              path="/see-dentist-billing-info"
              element={<SeeDentistBilling />}
            />
            <Route path="/see-dentist-review" element={<SeeDentistReview />} />
            <Route
              path="/appointment-schedule"
              element={<AppointmentSchedule />}
            />
            <Route
              path="/appointment-schedule-pharmacy-selection"
              element={<AppointmentSchedulePharmacySelection />}
            />
            <Route
              path="/appointment-schedule-billing"
              element={<AppointmentScheduleBilling />}
            />
            <Route
              path="/appointment-schedule-date-time"
              element={<AppointmentScheduleDateTimePage />}
            />
            <Route
              path="/appointment-schedule-review"
              element={<AppointmentScheduleReview />}
            />

            <Route
              path="/reschedule-virtual-appointment"
              element={<AppointmentReschedule />}
            />
            <Route path="/cancel-appointment" element={<AppointmentCancel />} />

            <Route path="/prescription" element={<Prescription />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/uploads" element={<Uploads />} />
            <Route path="/forms" element={<Forms />} />
            <Route
              path="/form-print/:medicalFormPatientId/:historyId"
              element={<FormPrint />}
            />
            <Route path="/form-submission" element={<FormSubmission />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/visit-history" element={<VisitHistory />} />
            <Route path="/education" element={<Education />} />
            <Route path="/videos" element={<Videos />} />
            <Route
              path="/insurance-information"
              element={<InsuranceInformationForm />}
            />
            <Route path="/my-dependents" element={<MyDependents />} />
            <Route path="/profile-setting" element={<ProfileSetting />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route
              path="/billing-information"
              element={<BillingInformation />}
            />
            <Route
              path="/prescription-print/:patientId/:prescriptionId"
              element={<PrescriptionPrint />}
            />
            <Route
              path="/notes-print/:noteId/:noteType"
              element={<NotesPrint />}
            />
            <Route
              path="/visit-history-print/:patientId/:completionDateAndTime/:consultationId"
              element={<VisitHistoryPrint />}
            />

            <Route path="/instruction" element={<Instruction />} />
            <Route path="/questionnaire" element={<Questionnaire />} />
            <Route
              path="/record-request"
              element={<QuestionnaireRecordRequest />}
            />

            <Route
              path="/questionnaire-insurance-info"
              element={<QuestionnaireInsuranceInfo />}
            />

            <Route
              path="/questionnaire-intake-form"
              element={<QuestionnaireIntakeForm />}
            />
            <Route
              path="/questionnaire-review-submit"
              element={<QuestionnaireReviewSubmit />}
            />
            <Route
              path="/questionnaire-billing-info"
              element={<QuestionnaireBillingInfo />}
            />
            <Route
              path="/QuestionnaireAfterPaymentSuccessful"
              element={<QuestionnaireAfterPaymentSuccessful />}
            />
            <Route path="/consent-form" element={<ConsentForm />} />
            <Route path="/case-review" element={<CaseReview />} />
            <Route
              path="/request-completed-details/:secondOpinionId"
              element={<RequestCompletedDetail />}
            />
            <Route
              path="/update-record"
              element={<QuestionnaireUpdateRecord />}
            />
            <Route
              path="/upload-record"
              element={<QuestionnaireUploadRecords />}
            />
            <Route
              path="/second-opinion-appointment"
              element={<SecondOpinionAppointment />}
            />
            <Route
              path="/second-opinion-appointment-reschedule"
              element={<SecondOpinionAppointmentReschedule />}
            />
            <Route path="/booked-appointment" element={<BookedAppointment />} />
            <Route
              path="/upload-more-records"
              element={<UploadMoreRecords />}
            />

            <Route path="/smart-scan-works" element={<SmartScanWorks />} />
            <Route
              path="/smart-scan-visit"
              element={<SmartScanSelectPatient />}
            />
            <Route
              path="/smart-scan-disclaimer"
              element={<SmartScanDisclaimer />}
            />
            <Route
              path="/smart-scan-photo-options"
              element={<SmartScanPhotoOption />}
            />
            <Route
              path="/smart-scan-instruction"
              element={<SmartScanInstruction />}
            />
            <Route
              path="/smart-scan-lower-arch"
              element={<SmartScanLowerArch />}
            />
            <Route
              path="/smart-scan-lower-arch-confirm"
              element={<SmartScanLowerArchConfirm />}
            />
            <Route
              path="/smart-scan-lower-lip"
              element={<SmartScanLowerLip />}
            />
            <Route
              path="/smart-scan-lower-lip-confirm"
              element={<SmartScanLowerLipConfirm />}
            />
            <Route
              path="/smart-scan-upper-arch"
              element={<SmartScanUpperArch />}
            />
            <Route
              path="/smart-scan-upper-arch-confirm"
              element={<SmartScanUpperArchConfirm />}
            />
            <Route
              path="/smart-scan-upper-lip"
              element={<SmartScanUpperLip />}
            />
            <Route
              path="/smart-scan-upper-lip-confirm"
              element={<SmartScanUpperLipConfirm />}
            />
            <Route path="/smart-scan-bite" element={<SmartScanBite />} />
            <Route
              path="/smart-scan-bite-confirm"
              element={<SmartScanBiteConfirm />}
            />
            <Route path="/smart-scan-pose" element={<SmartScanPose />} />
            <Route
              path="/smart-scan-submitted"
              element={<SmartScanSubmitted />}
            />
            <Route
              path="/smart-scan-submitted-records"
              element={<SmartScanSubmittedRecords />}
            />
            <Route
              path="/smart-scan-wellness-score"
              element={<WellnessScore />}
            />
            <Route
              path="/wellness-score-print/:smartScanId"
              element={<WellnessScorePrint />}
            />
            <Route path="/cancel-payment" element={<CancelPayment />} />

            <Route path="/chat" element={<Chat />} />
          </Route>
          <Route
            path="/dentist-direct-registration"
            element={<DentistDirectLandingPage />}
          />
          <Route path="/anonymous-cici-chat" element={<AnonymousChat />} />
          <Route
            path="/smartscan-report/:scrambledData"
            element={<SmartscanReport />}
          />
          <Route path="/r/:shortcode" element={<RedirectionToShortUrl />} />
        </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Navigation;
